/**
 * The salary PIN (ADR 0041): set, enter, change and reset the personal PIN that unlocks OTHER people's pay.
 *
 * Which responses are locked is not decided here — each pay-bearing route says so itself
 * (`routeMeta(..., { salaryPin })`, see meta.ts) and the API refuses it with `SALARY_PIN_REQUIRED` until this
 * login session has entered the PIN in the last five minutes. These routes are how the client answers that.
 *
 *   - Every route is `routeMeta(null, { ai: 'hidden' })`: signed-in only, and the handler refuses a caller who
 *     does not `needsSalaryPin` (they have nothing to unlock). Hidden from the copilot so a PIN is never typed
 *     into a chat.
 *   - Impersonation and platform-entered sessions can never set or enter a PIN; pay stays locked for them.
 *   - There is no lock route. Signing out ends the unlock, and it expires on its own after five minutes.
 *   - A wrong PIN is a 422 (`SALARY_PIN_INCORRECT`), never a 403, so the client's "PIN required" retry cannot
 *     loop on it. Five wrong PINs lock the PIN for five minutes (403 `SALARY_PIN_REQUIRED`, reason `locked`).
 *   - "Forgot PIN" proves the person's own phone with the same Firebase SMS proof as "Forgot password"
 *     (single-use, shared with sign-up and password reset), then sets a new PIN.
 *   - Someone with no verified phone (signed in by email) first confirms it is them — their password, and their
 *     authenticator code when two-factor is on — then proves a number by SMS, which becomes their verified phone,
 *     then sets the new PIN (`resetPinWithNewPhone`). The step-up stops someone at an unlocked session from
 *     verifying their OWN number and unlocking everyone's pay. A provider-only account has no step-up a person at
 *     the same browser could not pass in one click (the provider session is usually live), so it is not offered.
 *   - The way out for everyone else: an administrator holding `salary_pin:reset` (owner-only by default) clears
 *     the person's PIN (`adminResetPin`). The admin never sees or sets a PIN; the person creates a new one next
 *     time, and is emailed that it happened.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { routeMeta } from './meta.js';
import { phoneVerificationConfigSchema } from './auth.contract.js';
import { errorBodySchema, instantSchema, phoneSchema, uuidSchema } from '../schemas/common.js';
import { isWeakSalaryPin, SALARY_PIN_MAX_ATTEMPTS } from '../permissions/salary-pin.js';

const c = initContract();

/** What the PIN routes declare: signed-in only, never a copilot tool. */
const PIN_ROUTE = routeMeta(null, { ai: 'hidden' });

/** A NEW PIN — 4 to 6 digits, and not one a colleague would guess. */
export const salaryPinSchema = z
  .string()
  .regex(/^\d{4,6}$/, 'Use 4 to 6 digits')
  .refine(
    (pin) => !isWeakSalaryPin(pin),
    'Too easy to guess — avoid repeated or sequential digits',
  );

/** A PIN being ENTERED: only the shape. A weak PIN set before a rule change must still be enterable. */
const enteredSalaryPinSchema = z.string().regex(/^\d{4,6}$/, 'Use 4 to 6 digits');

export const salaryPinStatusSchema = z.object({
  /** False when the caller holds none of SALARY_PIN_PERMISSIONS — nothing is ever locked for them. */
  required: z.boolean(),
  isSet: z.boolean(),
  /** When this login session's unlock ends; null when locked. */
  unlockedUntil: instantSchema.nullable(),
  /**
   * How long this session's unlock has left, in ms; null when locked. Clients arm their relock timer from THIS and
   * their own clock, never from `unlockedUntil`, which is on the server's clock.
   */
  unlockedForMs: z.number().int().positive().nullable(),
  /** Lockout after too many wrong PINs; null when not locked. */
  lockedUntil: instantSchema.nullable(),
  attemptsRemaining: z.number().int().min(0).max(SALARY_PIN_MAX_ATTEMPTS),
  /** Impersonation / platform-entered sessions can never unlock. */
  canUnlock: z.boolean(),
  reset: z.object({
    /** Phone OTP is configured for this deployment AND the person has a verified number. */
    available: z.boolean(),
    /** The caller's own E.164 number the SMS goes to (credential.verifiedPhone only). */
    phone: z.string().nullable(),
    /** Same public Firebase web config SignupOptions carries; null when phone OTP is off. */
    phoneVerification: phoneVerificationConfigSchema.nullable(),
    /**
     * Phone OTP is configured but the caller has no verified number: "Forgot PIN" must first add one through
     * `resetPinWithNewPhone`. False when `phone` is set, or when phone OTP is off.
     */
    needsPhone: z.boolean(),
    /**
     * How the caller confirms it is them before adding a number: `password` when they have one; null when they
     * sign in only through a provider (no step-up is safe there — ask an administrator).
     */
    confirmWith: z.enum(['password']).nullable(),
    /** Two-factor is on: the step-up also takes the current authenticator code. */
    needsTotp: z.boolean(),
  }),
});
export type SalaryPinStatus = z.infer<typeof salaryPinStatusSchema>;

/** Every successful set / verify / change / reset unlocks this login session and says until when. */
export const salaryPinUnlockedSchema = z.object({
  unlockedUntil: instantSchema,
  /** The unlock's length in ms; see `unlockedForMs` on the status. */
  unlockedForMs: z.number().int().positive(),
});
export type SalaryPinUnlocked = z.infer<typeof salaryPinUnlockedSchema>;

export const setSalaryPinBodySchema = z.object({ pin: salaryPinSchema });
export type SetSalaryPinBody = z.infer<typeof setSalaryPinBodySchema>;

/** No weakness check on entry — see `enteredSalaryPinSchema`. */
export const verifySalaryPinBodySchema = z.object({ pin: enteredSalaryPinSchema });
export type VerifySalaryPinBody = z.infer<typeof verifySalaryPinBodySchema>;

export const changeSalaryPinBodySchema = z.object({
  currentPin: enteredSalaryPinSchema,
  newPin: salaryPinSchema,
});
export type ChangeSalaryPinBody = z.infer<typeof changeSalaryPinBodySchema>;

export const resetSalaryPinBodySchema = z.object({
  /** Firebase ID token from the SMS code to `status.reset.phone`. Single-use, shared with sign-up / password reset. */
  phoneProof: z.string().min(20).max(4096),
  newPin: salaryPinSchema,
});
export type ResetSalaryPinBody = z.infer<typeof resetSalaryPinBodySchema>;

export const resetSalaryPinWithNewPhoneBodySchema = z.object({
  /** The person's login password. A wrong one counts toward BOTH the PIN lockout and the login lockout. */
  password: z.string().min(1).max(256),
  /** Current authenticator code; required when `status.reset.needsTotp`. */
  totpCode: z
    .string()
    .regex(/^\d{6}$/)
    .optional(),
  /** The number to add, E.164. Becomes `credential.verifiedPhone` once the SMS proof matches it. */
  phone: phoneSchema,
  /** Firebase ID token from the SMS code to `phone`. Single-use, shared with sign-up / password reset. */
  phoneProof: z.string().min(20).max(4096),
  newPin: salaryPinSchema,
});
export type ResetSalaryPinWithNewPhoneBody = z.infer<typeof resetSalaryPinWithNewPhoneBodySchema>;

export const adminResetSalaryPinBodySchema = z.object({ personId: uuidSchema });
export type AdminResetSalaryPinBody = z.infer<typeof adminResetSalaryPinBodySchema>;

/** `hadPin` false: the person had no PIN (or it was already cleared) — nothing changed, nobody emailed. */
export const adminResetSalaryPinResultSchema = z.object({ hadPin: z.boolean() });
export type AdminResetSalaryPinResult = z.infer<typeof adminResetSalaryPinResultSchema>;

export const salaryPinContract = c.router(
  {
    /** The caller's own PIN state, and what the dialog needs to pick its step. */
    getStatus: {
      method: 'GET',
      path: '/status',
      responses: {
        200: salaryPinStatusSchema,
        401: errorBodySchema,
      },
      metadata: PIN_ROUTE,
      summary:
        "The caller's salary PIN state: set or not, unlocked until, locked until, reset options",
    },
    /** First PIN. 409 when one is already set — use change or reset instead. */
    setPin: {
      method: 'POST',
      path: '/set',
      body: setSalaryPinBodySchema,
      responses: {
        200: salaryPinUnlockedSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
        429: errorBodySchema,
      },
      metadata: PIN_ROUTE,
      summary: 'Create the salary PIN and unlock pay for this session',
    },
    /**
     * Enter the PIN. 422 `SALARY_PIN_INCORRECT` with `attemptsRemaining` on a wrong PIN; 403
     * `SALARY_PIN_REQUIRED` with reason `locked` (and `lockedUntil`) once locked, or `not_set`.
     */
    verifyPin: {
      method: 'POST',
      path: '/verify',
      body: verifySalaryPinBodySchema,
      responses: {
        200: salaryPinUnlockedSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
        429: errorBodySchema,
      },
      metadata: PIN_ROUTE,
      summary: 'Enter the salary PIN to unlock pay for five minutes',
    },
    /** Old PIN to new. A wrong current PIN counts toward the lockout exactly like verify. */
    changePin: {
      method: 'POST',
      path: '/change',
      body: changeSalaryPinBodySchema,
      responses: {
        200: salaryPinUnlockedSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
        429: errorBodySchema,
      },
      metadata: PIN_ROUTE,
      summary: 'Change the salary PIN',
    },
    /**
     * Forgot PIN: a fresh SMS proof for the caller's own number, then a new PIN. Clears any lockout. 401
     * (`details.reason: 'phone_unverified'`) when the proof is missing, reused or for another number; 503 when
     * phone verification is not configured on this deployment.
     */
    resetPin: {
      method: 'POST',
      path: '/reset',
      body: resetSalaryPinBodySchema,
      responses: {
        200: salaryPinUnlockedSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
        429: errorBodySchema,
        503: errorBodySchema,
      },
      metadata: PIN_ROUTE,
      summary: 'Reset a forgotten salary PIN after verifying your phone',
    },
    /**
     * Forgot PIN for someone with NO verified phone: confirm it is them (password, plus authenticator code when
     * two-factor is on), prove a new number by SMS, set a new PIN — one atomic call. The number becomes their
     * verified phone (so "Forgot password -> mobile code" works for them too), and they are emailed that it
     * happened. 422 with `details.reason`: `password_incorrect` (counts toward the PIN lockout and the login
     * lockout), `totp_required` or `totp_incorrect` — never 401, which the client answers by refreshing and resending
     * the same body; 401 `phone_unverified`; 403 `SALARY_PIN_REQUIRED` (`locked`) when that wrong password or code
     * used the PIN's last attempt, or for a provider-only account; 409
     * when the caller already has a verified phone (use `resetPin`) or the number is another account's verified
     * phone in this organisation; 503 when phone OTP is off.
     */
    resetPinWithNewPhone: {
      method: 'POST',
      path: '/reset-with-new-phone',
      body: resetSalaryPinWithNewPhoneBodySchema,
      responses: {
        200: salaryPinUnlockedSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
        429: errorBodySchema,
        503: errorBodySchema,
      },
      metadata: PIN_ROUTE,
      summary: 'Reset a forgotten salary PIN by confirming your password and verifying a new phone',
    },
    /**
     * An administrator clears another person's salary PIN and its lockout; that person creates a new PIN the next
     * time pay is asked for, and is emailed who reset it. Idempotent. 403 for the caller's own PIN (use "Forgot
     * PIN") and in an impersonation / platform-entered session; 404 when the person is not in this organisation.
     * Hidden from the copilot: a PIN reset is a security action a person takes deliberately, not a chat proposal.
     */
    adminResetPin: {
      method: 'POST',
      path: '/admin/reset',
      body: adminResetSalaryPinBodySchema,
      responses: {
        200: adminResetSalaryPinResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
        429: errorBodySchema,
      },
      metadata: routeMeta('salary_pin:reset', { ai: 'hidden' }),
      summary: "Reset another person's salary PIN so they create a new one",
    },
  },
  { pathPrefix: '/salary-pin' },
);
